import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomButton from "@/common/components/CustomButton";
import CustomInput from "@/common/components/CustomInput";
import SideModal from "@/common/components/CustomModal/SideModal";
import routes from "@/common/routes";
import ModalTitleHeader from "@/common/components/ModalTitleHeader";
import { useAppSelector } from "@/common/hooks";
import {
  iHandleChange,
  iHandleSubmit,
  IwithrawCrypto,
} from "../../../../types";
import {
  FormatNumber,
  getCurrentWalletType,
  removeCommas,
} from "@/common/utils/helpers";
import ModalNavLinkWithdrawal from "../../../../components/ModalNavLinkWithdrawal";
import Language from "@/common/utils/language/en";
import { Select, SelectItem } from "@/common/components/CustomSelect";
import { useBankWithdrawalMutation } from "@/module/wallet/service/api";

const { AuthorizeWithdrawal: PageDictionary } =
  Language.protected.walletModals.withdrawal;

const formatCyptoData = (data?: IwithrawCrypto, amount?: string) => {
  const temp: any[] = [];

  if (!data || !amount) return;

  temp.push({ title: "Amount", value: amount });
  (Object.keys(data) as Array<keyof typeof data>).forEach((key) => {
    let k_data: any = {};

    switch (key) {
      case "address":
      case "network":
        k_data = { title: key, value: data[key] };
        break;
      case "crypto_symbol":
        k_data = { title: "Coin", value: data[key] };
        break;

      default:
        break;
    }
    if (k_data.title) {
      temp.push(k_data);
    }
  });

  return temp;
};

function AuthorizeCrypto() {
  const { appid } = useParams();
  const [bankWithdrawal, { isLoading: loading }] = useBankWithdrawalMutation();
  const navigate = useNavigate();
  const location = useLocation().state;
  const background = location?.background;
  const amount = location?.amount;
  const { crypto } = useAppSelector((state) => state.wallet.withdrawal.data);
  const [password, setPassword] = useState("");
  // const temp_crypto = crypto && crypto?.length > 0 ? crypto[0] : undefined;
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [data, setData] = useState<any[]>();

  // const data = formatCyptoData(temp_crypto, FormatNumber(amount, true));

  const backHandler = () => {
    if (!appid || loading) return;
    const path = routes.protected.applications.app.wallet.withdrawl.link(appid);
    navigate(path, { state: { background, amount, isCrypto: true } });
  };

  const changeHandler: iHandleChange = useCallback((e) => {
    const { value } = e.target;
    setPassword(value);
  }, []);

  const submitHandler: iHandleSubmit = (e) => {
    e.preventDefault();

    if (!appid || !crypto) return;
    const success_path =
      routes.protected.applications.app.wallet.withdrawl.success.link(appid);
    const error_path = background?.pathname;

    const selectedData = crypto.find((d) => d.address === selectedAddress);

    if (!selectedData) return;

    const req = {
      appid,
      data: {
        amount: removeCommas(amount),
        password,
        withdrawal_mode: "CRYPTO",
        network: selectedData.network,
        wallet_type: getCurrentWalletType(background?.pathname),
      },
    };

    bankWithdrawal(req)
      .unwrap()
      .then(() => {
        navigate(success_path, {
          state: { background },
        });
      })
      .catch(() => {
        navigate(error_path, {
          state: { background },
        });
      });
  };

  const selectHandler = (val: string) => {
    setSelectedAddress(val);
  };

  useEffect(() => {
    if (selectedAddress && crypto && crypto?.length > 0) {
      const selectedData = crypto.find((d) => d.address === selectedAddress);

      if (selectedData)
        setData(formatCyptoData(selectedData, FormatNumber(amount, true)));
    }
  }, [selectedAddress, crypto, amount]);

  return (
    <SideModal
      onclickBackDrop={backHandler}
      className="p-8 md:pt-22 md:pl-15 md:pr-28"
    >
      <ModalTitleHeader
        title={PageDictionary.title}
        subtitle={PageDictionary.subtitle}
      />
      <div className="mt-9">
        <ModalNavLinkWithdrawal amount={amount} />
      </div>

      <form className="flex flex-col gap-y-10 py-12" onSubmit={submitHandler}>
        {crypto && crypto?.length > 0 && (
          <Select
            label="Select Address"
            value={selectedAddress}
            name="address"
            onChange={selectHandler}
            placeholder="Select Address"
            className=" overflow-x-hidden truncate"
          >
            {crypto.map((itm, idx) => (
              <SelectItem key={idx} value={itm.address} className="">
                <span className="">
                  {itm.address.length <= 30
                    ? itm.address
                    : itm.address.slice(0, 10) +
                      "********" +
                      itm.address.slice(
                        itm.address.length - 10,
                        itm.address.length
                      )}
                </span>
              </SelectItem>
            ))}
          </Select>
        )}
        <CustomInput
          label={PageDictionary.form.password_label}
          value={password}
          onChange={changeHandler}
          type="password"
          required
        />

        <div className="grid grid-cols-2 gap-y-7 ">
          {data && data?.length
            ? data?.map((itm) => (
                <div className="flex flex-col gap-y-1.5" key={itm?.title}>
                  <div className="text-sm leading-6 text-base-20">
                    {itm?.title}
                  </div>
                  <div className="text-lg font-bold leading-6 text-black break-words">
                    {itm?.value}
                  </div>
                </div>
              ))
            : null}
        </div>

        <CustomButton
          className="mt-3 self-start rounded-lg py-3 px-10 text-sm leading-6"
          disabled={!password || !selectedAddress}
          isloading={loading}
        >
          {PageDictionary.form.submit_btn}
        </CustomButton>
      </form>
    </SideModal>
  );
}

export default AuthorizeCrypto;
